/**
 * Topical clusters heatmap — matrix view.
 *
 * Renders the per-category rollup (A–G) computed by
 * services/report_enrichment.build_topical_clusters(). Each row shows the
 * presence / owned-authority / opportunity rate for that cluster plus a
 * static "FieldGuide lever" annotation that answers the strategic question
 * *"can we actually move this?"*.
 *
 * FIELDGUIDE_LEVERS is intentionally hardcoded here rather than emitted by
 * the backend because it's a positioning/strategy layer, not a computed
 * metric. Tuning the copy is a one-line change and lives with the UI.
 */
import { TESTIDS } from "@/constants/testIds";

/**
 * Static FieldGuide-lever mapping. Keyed by the cluster_id emitted by
 * services.query_generator.CATEGORY_ALLOCATION (A–G).
 * `fit` drives the badge treatment; `note` is the row explanation.
 */
const FIELDGUIDE_LEVERS = {
  A: {
    fit: "Indirect",
    note: "Brand & review-driven. FieldGuide moves this only via topical-authority halo.",
  },
  B: {
    fit: "Direct fit",
    note: "Itineraries, hidden-gem guides, rainy-day plans — FieldGuide's core output.",
  },
  C: {
    fit: "Direct fit",
    note: "Visit-purpose guides tuned to each local demand generator (university, hospital, venue).",
  },
  D: {
    fit: "Strong fit",
    note: "Guides tuned by audience (families, couples, business travelers) — FieldGuide personas.",
  },
  E: {
    fit: "Direct fit",
    note: "Neighborhood dining and evening-out guides — the property's staff know these best.",
  },
  F: {
    fit: "Strong fit",
    note: "Seasonal and event-tied guides refreshed 3–4× per year.",
  },
  G: {
    fit: "Strong fit",
    note: "Arrival & orientation content — parking, walkability, transit tips.",
  },
};

// Continuous heat scale for the "Present" and "Owned" columns (higher = better)
// and the "Opportunity" column (higher = bigger unmet gap).
function presenceStyle(pct) {
  if (pct >= 75) return { bg: "rgba(76,155,92,0.36)", color: "#2e6b3a" };
  if (pct >= 50) return { bg: "rgba(76,155,92,0.22)", color: "#2e6b3a" };
  if (pct >= 25) return { bg: "rgba(201,154,63,0.28)", color: "#7d5f21" };
  if (pct > 0)   return { bg: "rgba(201,154,63,0.16)", color: "#7d5f21" };
  return { bg: "rgba(184,84,80,0.14)", color: "#7a3532" };
}

function opportunityStyle(pct) {
  if (pct >= 100) return { bg: "rgba(166,124,63,0.62)", color: "#3c2a10" };
  if (pct >= 75)  return { bg: "rgba(166,124,63,0.44)", color: "#4d3617" };
  if (pct >= 50)  return { bg: "rgba(166,124,63,0.28)", color: "#6d4d21" };
  if (pct >= 25)  return { bg: "rgba(166,124,63,0.18)", color: "#6d4d21" };
  return { bg: "rgba(120,110,88,0.10)", color: "var(--text-secondary)" };
}

function fitBadgeStyle(fit) {
  if (fit === "Direct fit")  return { bg: "rgba(76,155,92,0.15)", color: "#2e6b3a" };
  if (fit === "Strong fit")  return { bg: "rgba(107,122,85,0.16)", color: "var(--brand-sage-deep)" };
  return { bg: "rgba(120,110,88,0.14)", color: "var(--text-secondary)" }; // Indirect
}

export default function TopicalClustersHeatmap({ clusters }) {
  if (!clusters || clusters.length === 0) return null;

  // Summary stat for the intro sentence — how many clusters are
  // FieldGuide-addressable (Direct or Strong fit) with a meaningful gap?
  const addressable = clusters.filter((c) => {
    const lever = FIELDGUIDE_LEVERS[c.cluster_id];
    return lever && lever.fit !== "Indirect" && c.opportunity_rate >= 25;
  });
  const totalTested = clusters.reduce((s, c) => s + (c.queries_tested || 0), 0);

  return (
    <div
      className="mt-10 fade-in"
      data-testid={TESTIDS.reportTopicalClusters}
    >
      <div className="mb-5">
        <div className="label-eyebrow mb-2">Topical breakdown</div>
        <h3 className="font-serif text-2xl md:text-3xl leading-tight">
          Where your local authority stands across seven fronts
        </h3>
        <p
          className="text-base leading-relaxed mt-3 max-w-3xl"
          style={{ color: "var(--text-secondary)" }}
        >
          Every one of the {totalTested} queries we tested belongs to one of seven topical clusters. Here&apos;s
          the rollup — plus, for each, how much of it FieldGuide can actually help you own.
          {addressable.length > 0 && (
            <>
              {" "}
              <span className="font-medium" style={{ color: "var(--text-primary)" }}>
                {addressable.length} of {clusters.length}
              </span>{" "}
              are direct or strong fits for FieldGuide-generated content.
            </>
          )}
        </p>
      </div>

      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-default)",
          boxShadow: "0 8px 28px -18px rgba(60,45,20,0.18)",
        }}
      >
        {/* Header row */}
        <div
          className="hidden md:grid grid-cols-[minmax(0,1.4fr)_90px_90px_100px_minmax(0,1.5fr)] gap-3 px-6 py-3 text-xs font-medium uppercase tracking-widest"
          style={{
            color: "var(--text-secondary)",
            borderBottom: "1px solid var(--border-default)",
            background: "var(--bg-card-warm)",
          }}
        >
          <div>Cluster</div>
          <div className="text-center">Present</div>
          <div className="text-center">Owned</div>
          <div className="text-center">Opportunity</div>
          <div>FieldGuide lever</div>
        </div>

        {/* Rows */}
        {clusters.map((c) => {
          const lever = FIELDGUIDE_LEVERS[c.cluster_id] || {
            fit: "Indirect",
            note: "—",
          };
          const pres = presenceStyle(c.presence_rate);
          const own = presenceStyle(c.owned_authority_rate);
          const opp = opportunityStyle(c.opportunity_rate);
          const fit = fitBadgeStyle(lever.fit);

          return (
            <div
              key={c.cluster_id}
              className="grid grid-cols-1 md:grid-cols-[minmax(0,1.4fr)_90px_90px_100px_minmax(0,1.5fr)] gap-3 md:gap-3 px-6 py-4 items-center"
              style={{ borderBottom: "1px solid rgba(120,110,88,0.10)" }}
              data-testid={`cluster-row-${c.cluster_id}`}
            >
              {/* Cluster label */}
              <div className="flex items-baseline gap-3 min-w-0">
                <span
                  className="inline-flex items-center justify-center flex-shrink-0"
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    background: "var(--bg-card-warm)",
                    color: "var(--brand-sage-deep)",
                    fontFamily: "ui-sans-serif, system-ui",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                  aria-label={`Cluster ${c.cluster_id}`}
                >
                  {c.cluster_id}
                </span>
                <span className="min-w-0">
                  <span
                    className="font-serif text-[15px] md:text-base"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {c.cluster_label}
                  </span>
                  <span
                    className="text-xs ml-2"
                    style={{ color: "var(--text-muted)" }}
                  >
                    · {c.queries_tested} {c.queries_tested === 1 ? "query" : "queries"}
                  </span>
                </span>
              </div>

              {/* Percent pills — labeled on mobile, header-implied on desktop */}
              <div className="flex md:justify-center gap-2 items-center">
                <span className="md:hidden text-xs" style={{ color: "var(--text-secondary)" }}>Present</span>
                <Pill value={c.presence_rate} style={pres} />
              </div>
              <div className="flex md:justify-center gap-2 items-center">
                <span className="md:hidden text-xs" style={{ color: "var(--text-secondary)" }}>Owned</span>
                <Pill value={c.owned_authority_rate} style={own} />
              </div>
              <div className="flex md:justify-center gap-2 items-center">
                <span className="md:hidden text-xs" style={{ color: "var(--text-secondary)" }}>Opportunity</span>
                <Pill value={c.opportunity_rate} style={opp} />
              </div>

              {/* Lever */}
              <div className="text-sm leading-relaxed" style={{ color: "var(--text-primary)" }}>
                <span
                  className="inline-block px-2 py-0.5 rounded font-semibold uppercase mr-2 align-middle"
                  style={{
                    background: fit.bg,
                    color: fit.color,
                    fontFamily: "ui-sans-serif, system-ui",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                  }}
                >
                  {lever.fit}
                </span>
                <span style={{ color: "var(--text-secondary)" }}>{lever.note}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Pill({ value, style }) {
  return (
    <span
      className="inline-block px-2.5 py-1 rounded-full text-[13px] font-medium"
      style={{
        background: style.bg,
        color: style.color,
        fontFamily: "ui-sans-serif, system-ui",
        fontVariantNumeric: "tabular-nums",
        minWidth: 48,
        textAlign: "center",
      }}
    >
      {value}%
    </span>
  );
}
